import React, { useEffect, useRef, useState } from 'react';
import styles from './Sparkline.module.css';

export interface SparklineProps {
  /**
   * Array of numeric data points to plot
   */
  data: number[];

  /**
   * Width of the sparkline in pixels
   */
  width?: number;

  /**
   * Height of the sparkline in pixels
   */
  height?: number;

  /**
   * Stroke color for the line (CSS color value)
   */
  color?: string;

  /**
   * Width of the stroke line in pixels
   */
  strokeWidth?: number;

  /**
   * Show animated dot at the end point
   */
  showDot?: boolean;

  /**
   * Show gradient fill under the line
   */
  showFill?: boolean;

  /**
   * Additional CSS class name
   */
  className?: string;
}

/**
 * Sparkline - A mini line chart for visualizing trends
 *
 * Displays a simple line chart with optional gradient fill and animated end dot.
 * Perfect for showing metric trends in dashboard stat cards.
 *
 * @example
 * ```tsx
 * <Sparkline
 *   data={[10, 15, 12, 18, 22, 20, 25]}
 *   color="var(--color-success)"
 *   showDot
 *   showFill
 * />
 * ```
 */
export const Sparkline: React.FC<SparklineProps> = ({
  data,
  width = 120,
  height = 40,
  color = 'currentColor',
  strokeWidth = 2,
  showDot = true,
  showFill = true,
  className,
}) => {
  const [isAnimated, setIsAnimated] = useState(false);
  const pathRef = useRef<SVGPathElement>(null);

  // Trigger animation on mount
  useEffect(() => {
    setIsAnimated(true);
  }, []);

  // Calculate path data
  const { pathData, fillPathData, lastPoint } = React.useMemo(() => {
    if (data.length < 2) {
      return { pathData: '', fillPathData: '', lastPoint: null };
    }

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1; // Avoid division by zero

    // Add padding to prevent clipping
    const padding = strokeWidth + 2;
    const graphWidth = width - padding * 2;
    const graphHeight = height - padding * 2;

    // Calculate points
    const points = data.map((value, index) => {
      const x = (index / (data.length - 1)) * graphWidth + padding;
      const y = height - ((value - min) / range) * graphHeight - padding;
      return { x, y };
    });

    // Create line path
    const pathData = points
      .map((point, index) => {
        const command = index === 0 ? 'M' : 'L';
        return `${command} ${point.x},${point.y}`;
      })
      .join(' ');

    // Create fill path (same as line but closed at bottom)
    const fillPathData =
      pathData +
      ` L ${points[points.length - 1].x},${height} L ${points[0].x},${height} Z`;

    return {
      pathData,
      fillPathData,
      lastPoint: points[points.length - 1],
    };
  }, [data, width, height, strokeWidth]);

  // Calculate path length for animation
  const pathLength = pathRef.current?.getTotalLength() || 0;

  const containerClasses = [styles.container, className]
    .filter(Boolean)
    .join(' ');

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={containerClasses}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Trend line showing ${data.length} data points`}
    >
      {/* Gradient definition for fill */}
      {showFill && (
        <defs>
          <linearGradient id="sparkline-gradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}

      {/* Filled area under the line */}
      {showFill && fillPathData && (
        <path
          d={fillPathData}
          fill="url(#sparkline-gradient)"
          className={isAnimated ? styles.fillAnimated : styles.fill}
        />
      )}

      {/* Main line */}
      {pathData && (
        <path
          ref={pathRef}
          d={pathData}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isAnimated ? styles.lineAnimated : styles.line}
          style={
            {
              '--path-length': pathLength,
            } as React.CSSProperties
          }
        />
      )}

      {/* Animated dot at end point */}
      {showDot && lastPoint && (
        <g>
          {/* Glow effect */}
          <circle
            cx={lastPoint.x}
            cy={lastPoint.y}
            r="5"
            fill={color}
            opacity="0.3"
            className={styles.dotGlow}
          />
          {/* Main dot */}
          <circle
            cx={lastPoint.x}
            cy={lastPoint.y}
            r="3"
            fill={color}
            className={isAnimated ? styles.dotAnimated : styles.dot}
          />
        </g>
      )}
    </svg>
  );
};
